import styled from '@emotion/styled';
import Radio from '@mui/material/Radio';
import { styled as muiStyled } from '@mui/material/styles';
import is from '@sindresorhus/is';
import type { FunctionComponent, MouseEventHandler, PropsWithChildren } from 'react';

import { theme } from '~/ui/theme/cobrainer-theme';

import { CustomThemeProvider } from '../../app/theming';
import { CheckCircleIcon } from '../icons/CheckCircleIcon';
import { EmptyCircleIcon } from '../icons/EmptyCircleIcon';

const RadioStyled = muiStyled(Radio)({
    color: theme.colors.gray[500],
});

const MenuItem = styled.div<{ readonly active: boolean }>(({ active }) => ({
    alignItems: 'flex-start',
    backgroundColor: active ? theme.colors.gray[200] : theme.colors.gray[100],
    borderRadius: theme.borderRadius.m,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    padding: `${theme.spacing.m} ${theme.spacing.m} ${theme.spacing.m} 0`,
}));

const MenuItemBox = styled.div({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
});

const MenuItemDescriptionBox = styled.div({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
});

const MenuItemTitle = styled.p({
    color: theme.colors.black[900],
    fontFamily: theme.typography.body.S.semibold.fontFamily,
    fontSize: theme.typography.body.S.semibold.fontSize,
    fontWeight: theme.typography.body.S.semibold.fontWeight,
    lineHeight: theme.typography.body.S.semibold.lineHeight,
    margin: 0,
});

const MenuItemSubtitle = styled.p({
    color: theme.colors.gray[800],
    fontFamily: theme.typography.body.XS.regular.fontFamily,
    fontSize: theme.typography.body.XS.regular.fontSize,
    fontWeight: theme.typography.body.XS.regular.fontWeight,
    lineHeight: theme.typography.body.S.regular.lineHeight,
    margin: 0,
});

const MenuItemSubSubtitle = styled.p({
    color: theme.colors.orange[900],
    fontFamily: theme.typography.body.XXS.medium.fontFamily,
    fontSize: theme.typography.body.XXS.medium.fontSize,
    fontWeight: theme.typography.body.XXS.medium.fontWeight,
    lineHeight: theme.typography.body.XXS.medium.lineHeight,
    margin: 0,
});

const ChildrenBox = styled.div({
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.xl,
    width: '100%',
});

export const JobLevelGenerateWithAiSettingsMenuItem: FunctionComponent<
    PropsWithChildren<{
        readonly checked: boolean;
        readonly onClick: MouseEventHandler;
        readonly subSubtitle?: string;
        readonly subtitle: string;
        readonly title: string;
    }>
> = ({ checked, children, onClick, subSubtitle, subtitle, title }) => {
    return (
        <CustomThemeProvider>
            <MenuItem active={checked} onClick={onClick}>
                <MenuItemBox>
                    <RadioStyled
                        checked={checked}
                        checkedIcon={<CheckCircleIcon />}
                        disableRipple={true}
                        icon={<EmptyCircleIcon />}
                    />
                    <MenuItemDescriptionBox>
                        <MenuItemTitle>{title}</MenuItemTitle>
                        <MenuItemSubtitle>{subtitle}</MenuItemSubtitle>
                        <MenuItemSubSubtitle>{subSubtitle}</MenuItemSubSubtitle>
                    </MenuItemDescriptionBox>
                </MenuItemBox>
                {checked && is.truthy(children) ? <ChildrenBox>{children}</ChildrenBox> : null}
            </MenuItem>
        </CustomThemeProvider>
    );
};
